import { useTranslation } from "react-i18next";
import { ReadonlyQuestionDisplay } from "./readonly-question-display";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";

interface AiReviewResult {
  isCorrect: boolean;
  methodSuitable: boolean;
  hintsIfNotCorrect?: string;
  errorType?: string;
}

interface AiReviewData {
  results: AiReviewResult[];
}

function manualJsonParse(jsonStr: string): AiReviewData | null {
  let i = 0;

  const ws = () => {
    while (i < jsonStr.length && ' \t\n\r'.includes(jsonStr[i])) i++;
  };

  const str = (raw = false): string => {
    if (jsonStr[i] !== '"') throw new Error('Expected "');
    i++;
    let s = '';
    while (i < jsonStr.length) {
      const c = jsonStr[i];
      if (c === '"') {
        i++;
        return s;
      }
      if (c === '\\' && i + 1 < jsonStr.length) {
        if (raw) {
          s += c;
          i++;
          s += jsonStr[i];
          i++;
        } else {
          i++;
          const esc = jsonStr[i];
          if (esc === 'n') s += '\n';
          else if (esc === 't') s += '\t';
          else if (esc === 'r') s += '\r';
          else if (esc === 'b') s += '\b';
          else if (esc === 'f') s += '\f';
          else if (esc === '\\') s += '\\';
          else if (esc === '"') s += '"';
          else if (esc === '/') s += '/';
          else if (esc === 'u' && i + 4 < jsonStr.length) {
            const hex = jsonStr.slice(i + 1, i + 5);
            s += String.fromCharCode(parseInt(hex, 16));
            i += 4;
          } else s += esc;
          i++;
        }
      } else {
        s += c;
        i++;
      }
    }
    throw new Error('Unclosed string');
  };

  const bool = (): boolean => {
    if (jsonStr.slice(i, i + 4) === 'true') {
      i += 4;
      return true;
    }
    if (jsonStr.slice(i, i + 5) === 'false') {
      i += 5;
      return false;
    }
    throw new Error('Expected boolean');
  };

  const nul = (): null => {
    if (jsonStr.slice(i, i + 4) === 'null') {
      i += 4;
      return null;
    }
    throw new Error('Expected null');
  };

  const obj = (): Record<string, string | boolean | null> => {
    if (jsonStr[i] !== '{') throw new Error('Expected {');
    i++;
    ws();
    const o: Record<string, string | boolean | null> = {};

    if (jsonStr[i] === '}') {
      i++;
      return o;
    }

    while (true) {
      ws();
      const k = str();
      ws();
      if (jsonStr[i] !== ':') throw new Error('Expected :');
      i++;
      ws();

      const c = jsonStr[i];
      let v: string | boolean | null;
      if (c === '"') v = str();
      else if (c === 't' || c === 'f') v = bool();
      else if (c === 'n') v = nul();
      else throw new Error('Unexpected value type');

      o[k] = v;

      ws();
      if (jsonStr[i] === '}') break;
      if (jsonStr[i] !== ',') throw new Error('Expected , or }');
      i++;
    }

    i++;
    return o;
  };

  const arr = (): Array<Record<string, string | boolean | null>> => {
    if (jsonStr[i] !== '[') throw new Error('Expected [');
    i++;
    ws();
    const a: Array<Record<string, string | boolean | null>> = [];

    if (jsonStr[i] === ']') {
      i++;
      return a;
    }

    while (true) {
      ws();
      a.push(obj());
      ws();
      if (jsonStr[i] === ']') break;
      if (jsonStr[i] !== ',') throw new Error('Expected , or ]');
      i++;
    }

    i++;
    return a;
  };

  try {
    ws();
    if (jsonStr[i] !== '{') throw new Error('Expected root {');
    i++;
    ws();

    let results: AiReviewResult[] = [];

    while (jsonStr[i] !== '}') {
      ws();
      const k = str();
      ws();
      if (jsonStr[i] !== ':') throw new Error('Expected :');
      i++;
      ws();

      if (k === 'results') {
        const rawArr = arr();
        results = rawArr.map(item => ({
          isCorrect: typeof item.isCorrect === 'boolean' ? item.isCorrect : false,
          methodSuitable: typeof item.methodSuitable === 'boolean' ? item.methodSuitable : false,
          hintsIfNotCorrect: typeof item.hintsIfNotCorrect === 'string' ? item.hintsIfNotCorrect : undefined,
          errorType: typeof item.errorType === 'string' ? item.errorType : undefined,
        }));
      } else {
        // Skip unknown fields
        if (jsonStr[i] === '"') str();
        else if (jsonStr[i] === 't' || jsonStr[i] === 'f') bool();
        else if (jsonStr[i] === 'n') nul();
        else if (jsonStr[i] === '{') obj();
        else if (jsonStr[i] === '[') arr();
      }

      ws();
      if (jsonStr[i] === '}') break;
      if (jsonStr[i] !== ',') throw new Error('Expected , or }');
      i++;
    }

    return { results };
  } catch (e) {
    console.error('Manual parse error:', e);
    return null;
  }
}

function parseAiReview(raw: string): AiReviewData | null {
  try {
    const parsed = manualJsonParse(raw);

    if (parsed && Array.isArray(parsed.results)) {
      return parsed as AiReviewData;
    }
    return null;
  } catch (e) {
    console.error("Error parsing AI Review JSON:", e, "Original:", raw);
    return null;
  }
}

interface AiReviewDisplayProps {
  aiReview: string;
}

interface ResultCardProps {
  result: AiReviewResult;
  index: number;
  showIndex: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

function AiReviewResultCard({ result, index, showIndex, t }: ResultCardProps) {
  const allCorrect = result.isCorrect;
  const answerCorrectMethodWrong = result.isCorrect && !result.methodSuitable;

  return (
    <div className={`rounded-lg border p-4 ${allCorrect ? "bg-green-50 border-green-200" : answerCorrectMethodWrong ? "bg-yellow-50 border-yellow-200" : "bg-red-50 border-red-200"}`}>
      {showIndex && (
        <div className="text-sm font-semibold text-gray-500 mb-2">
          {t("component.aiReviewPart", { part: index + 1 })}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-3">
        <StatusBadge
          value={result.isCorrect}
          trueLabel={t("component.aiReviewCorrect")}
          falseLabel={t("component.aiReviewIncorrect")}
        />
        {/* <StatusBadge
          value={result.methodSuitable}
          trueLabel={t("component.aiReviewMethodSuitable")}
          falseLabel={t("component.aiReviewMethodUnsuitable")}
        /> */}
      </div>

      {result.errorType && !result.isCorrect && (
        <div className="flex items-center gap-1.5 text-sm text-orange-700 mb-3">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{result.errorType}</span>
        </div>
      )}

      {result.hintsIfNotCorrect && (
        <div className="mt-2">
          <div className="text-sm font-medium text-gray-600 mb-1">
            {t("component.aiReviewHints")}
          </div>
          <ReadonlyQuestionDisplay
            questionText={result.hintsIfNotCorrect}
            className="flex flex-col text-gray-800 text-sm"
          />
        </div>
      )}
    </div>
  );
}

interface StatusBadgeProps {
  value: boolean;
  trueLabel: string;
  falseLabel: string;
}

function StatusBadge({ value, trueLabel, falseLabel }: StatusBadgeProps) {
  return (
    <div className={`flex items-center gap-1 text-sm font-medium px-2.5 py-1 rounded-full ${value ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
      {value
        ? <CheckCircle className="w-3.5 h-3.5" />
        : <XCircle className="w-3.5 h-3.5" />
      }
      <span>{value ? trueLabel : falseLabel}</span>
    </div>
  );
}

export function AiReviewDisplay({ aiReview }: AiReviewDisplayProps) {
  const { t } = useTranslation();
  const data = parseAiReview(aiReview);
  console.log("Parsed AI Review Data:", aiReview, data);

  if (!data) {
    return (
      <ReadonlyQuestionDisplay
        questionText={aiReview}
        className="flex flex-col text-gray-800"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {data.results.map((result, index) => (
        <AiReviewResultCard
          key={index}
          result={result}
          index={index}
          showIndex={data.results.length > 1}
          t={t}
        />
      ))}
    </div>
  );
}
